import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { ConsoleResponse, Project } from "../types/api";
import { SelfUploadModal } from "./SelfUploadModal";

interface UnifiedConsoleProps {
  currentUserId?: number;
  currentUserName?: string;
  currentUserRole?: string;
  // Where the Projects metric card leads when the viewer has more than one
  // project. Only the caller knows this: inside the admin shell there is a
  // real projects list at /admin/projects, whereas the standalone console at
  // /projects already *is* the researcher's project list.
  projectsRoute?: string;
}

// A viewer's per-project role label. A global `manager` sees every project
// in their org via org-wide oversight, not a personal assignment — labeled
// "Org Manager" to distinguish it from someone explicitly added to a single
// project as a project-level pi/manager ("Manager"). See the console design
// discussion: these are two genuinely different things that happen to share
// the word "manager" in the underlying role model.
function projectRoleLabel(
  projectRole: string | undefined,
  viewerGlobalRole: string | undefined,
): string {
  if (viewerGlobalRole === "manager") return "Org Manager";
  if (projectRole === "pi" || projectRole === "manager") return "Manager";
  if (projectRole === "researcher") return "Researcher";
  return projectRole
    ? projectRole.charAt(0).toUpperCase() + projectRole.slice(1)
    : "—";
}

const formatShortDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

interface ProjectRowProps {
  project: Project;
  currentUserId?: number;
  currentUserRole?: string;
  onClick: () => void;
}

const ProjectRow: React.FC<ProjectRowProps> = ({
  project,
  currentUserId,
  currentUserRole,
  onClick,
}) => (
  <div className="console-project-row" onClick={onClick}>
    <span
      className="console-status-dot"
      style={{
        background: project.is_active
          ? "var(--text-success)"
          : "var(--text-muted)",
      }}
    />
    <div className="console-project-text">
      <p className="console-project-title">
        {project.title}
        {project.created_by_user_id != null &&
          project.created_by_user_id === currentUserId && (
            <span className="badge owner" style={{ marginLeft: 6 }}>
              Owner
            </span>
          )}
      </p>
    </div>
    {(() => {
      const label = projectRoleLabel(project.user_role, currentUserRole);
      return label === "Researcher" ? (
        <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
          {label}
        </span>
      ) : (
        <span className="badge pi">{label}</span>
      );
    })()}
  </div>
);

export const UnifiedConsole: React.FC<UnifiedConsoleProps> = ({
  currentUserId,
  currentUserName,
  currentUserRole,
  projectsRoute,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState<ConsoleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [selectedProjectForUpload, setSelectedProjectForUpload] = useState<
    number | null
  >(null);

  useEffect(() => {
    loadConsole();
  }, []);

  const loadConsole = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getConsole();
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load console");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "48px", textAlign: "center", color: "var(--text-muted)" }}>
        Loading...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        style={{
          padding: "20px",
          borderRadius: "var(--radius)",
          background: "var(--bg-danger)",
          color: "var(--text-danger)",
        }}
      >
        {error || "No console data available"}
      </div>
    );
  }

  const yourProjects = data.your_projects ?? [];
  const projectCount = yourProjects.length;
  // Self-upload ("De-identify a File") is a project-admin action — only
  // offer projects the viewer actually manages (pi, or manager via either a
  // per-project assignment or org-wide oversight), matching the backend's
  // requireProjectManage gate on the self-upload endpoint.
  const manageableProjects = yourProjects.filter(
    (p) => p.user_role === "pi" || p.user_role === "manager",
  );

  const projectsTarget =
    projectCount === 1
      ? `/projects/${yourProjects[0].id}`
      : projectCount > 1
        ? projectsRoute
        : undefined;

  return (
    <div>
      <style>{`
        .console-heading-label { font-size: 12px; color: var(--text-muted); margin: 0 0 2px; }
        .console-heading { font-size: 20px; font-weight: 700; color: var(--text-primary); margin: 0 0 20px; font-family: var(--font-display); }
        .console-metric-row { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
        .console-metric-card {
          flex: 1; min-width: 140px; padding: 14px 16px; border-radius: var(--radius);
          background: var(--surface-1); border: 0.5px solid var(--border); cursor: pointer;
        }
        .console-metric-card:hover { border-color: var(--border-strong); }
        .console-metric-card:focus-visible {
          outline: 2px solid var(--border-accent); outline-offset: 2px;
        }
        .console-metric-card.static { cursor: default; }
        .console-metric-card.static:hover { border-color: var(--border); }
        .console-metric-value { font-size: 24px; font-weight: 600; color: var(--text-primary); }
        .console-metric-label { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
        .console-actions { display: flex; gap: 10px; margin-bottom: 24px; flex-wrap: wrap; }
        .console-action-btn {
          padding: 9px 16px; border-radius: var(--radius); border: 0.5px solid var(--border-strong);
          background: var(--surface-2); color: var(--text-primary); font-weight: 600; font-size: 14px;
          cursor: pointer; font-family: inherit;
        }
        .console-action-btn:hover { background: var(--surface-1); }
        .console-section-label { font-size: 13px; font-weight: 700; color: var(--text-secondary); margin: 0 0 10px; }
        .console-section { margin-bottom: 24px; }
        .console-section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .console-view-all { font-size: 13px; color: var(--text-accent); background: none; border: none; cursor: pointer; font-family: inherit; }
        .console-attention-row, .console-project-row {
          display: flex; align-items: center; gap: 10px; padding: 10px 4px;
          border-bottom: 0.5px solid var(--border); cursor: pointer;
        }
        .console-attention-row:last-child, .console-project-row:last-child { border-bottom: none; }
        .console-attention-row:hover, .console-project-row:hover { background: var(--surface-1); }
        .console-status-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .console-project-text, .console-attention-text { flex: 1; min-width: 0; }
        .console-project-title, .console-attention-title { margin: 0; font-size: 14px; color: var(--text-primary); font-weight: 600; }
        .console-attention-sub { margin: 2px 0 0; font-size: 12px; color: var(--text-muted); }
        .console-review-btn {
          padding: 5px 12px; border-radius: var(--radius); border: 0.5px solid var(--border-strong);
          background: var(--surface-2); font-size: 13px; cursor: pointer; font-family: inherit;
        }
        .console-empty-state { padding: 16px 4px; color: var(--text-muted); font-size: 13px; }
      `}</style>

      <p className="console-heading-label">Welcome back</p>
      <h1 className="console-heading">{currentUserName || "there"}</h1>

      <div className="console-metric-row">
        {/* A single project jumps straight into it; several lead to the
            projects list when there is one. With neither there is nowhere to
            go, so the card renders as plain text rather than as a control
            that does nothing when clicked. */}
        <div
          className={`console-metric-card ${projectsTarget ? "" : "static"}`}
          onClick={
            projectsTarget ? () => navigate(projectsTarget) : undefined
          }
          role={projectsTarget ? "button" : undefined}
          tabIndex={projectsTarget ? 0 : undefined}
          onKeyDown={
            projectsTarget
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(projectsTarget);
                  }
                }
              : undefined
          }
        >
          <div className="console-metric-value">{data.project_count}</div>
          <div className="console-metric-label">
            {data.project_count === 1 ? "Project" : "Projects"}
          </div>
        </div>
        <div className="console-metric-card" onClick={() => navigate("/requests")}>
          <div className="console-metric-value">{data.data_request_count}</div>
          <div className="console-metric-label">Data Requests</div>
        </div>
        <div
          className="console-metric-card"
          onClick={() =>
            navigate("/datasets", { state: { filterType: "downloadable-only" } })
          }
        >
          <div className="console-metric-value">{data.available_dataset_count}</div>
          <div className="console-metric-label">Available Datasets</div>
        </div>
      </div>

      <div className="console-actions">
        <button className="console-action-btn" onClick={() => navigate("/datasets")}>
          Browse Datasets
        </button>
        <button className="console-action-btn" onClick={() => navigate("/requests/new")}>
          New Data Request
        </button>
        {manageableProjects.length > 0 && (
          <button
            className="console-action-btn"
            onClick={() => {
              if (manageableProjects.length === 1) {
                setSelectedProjectForUpload(manageableProjects[0].id);
              } else {
                setShowProjectSelector(true);
              }
            }}
          >
            De-identify a File
          </button>
        )}
      </div>

      {data.needs_attention !== null && (
        <div className="console-section">
          <p className="console-section-label">Needs your attention</p>
          {data.needs_attention.length === 0 ? (
            <p className="console-empty-state">Nothing to review right now.</p>
          ) : (
            <div>
              {data.needs_attention.map((request) => (
                <div
                  className="console-attention-row"
                  key={request.id}
                  onClick={() => navigate(`/admin/requests/${request.id}`)}
                >
                  <div className="console-attention-text">
                    <p className="console-attention-title">
                      {request.user_name ? `${request.user_name} requests ` : ""}
                      {request.title}
                    </p>
                    <p className="console-attention-sub">
                      {request.project_title || `Project ${request.project_id}`}
                      {" · Requested "}
                      {formatShortDate(request.created_at)}
                    </p>
                  </div>
                  <button
                    className="console-review-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/requests/${request.id}`);
                    }}
                  >
                    Review
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="console-section">
        <div className="console-section-header">
          <p className="console-section-label" style={{ margin: 0 }}>
            Your requests
          </p>
          <button className="console-view-all" onClick={() => navigate("/requests")}>
            View all
          </button>
        </div>
        <p style={{ fontSize: "13px", color: "var(--text-secondary)", margin: 0 }}>
          {data.your_requests.pending} pending · {data.your_requests.resolved} resolved
        </p>
      </div>

      <div className="console-section">
        <p className="console-section-label">Your projects</p>
        {projectCount === 0 ? (
          <p className="console-empty-state">
            {currentUserRole === "manager" || currentUserRole === "pi"
              ? "No projects yet. Create one to get started."
              : "Contact an administrator to get access to a research project."}
          </p>
        ) : (
          <div>
            {yourProjects.map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                currentUserId={currentUserId}
                currentUserRole={currentUserRole}
                onClick={() => navigate(`/projects/${project.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {showProjectSelector && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999,
            padding: "16px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowProjectSelector(false);
          }}
        >
          <div
            style={{
              backgroundColor: "var(--surface-2)",
              borderRadius: "var(--radius)",
              width: "100%",
              maxWidth: "400px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "0.5px solid var(--border)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: "16px" }}>Select Project</div>
              <button
                onClick={() => setShowProjectSelector(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "var(--text-muted)",
                  lineHeight: 1,
                  padding: "0 4px",
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: "16px 20px" }}>
              <p style={{ fontSize: "14px", color: "var(--text-muted)", marginBottom: "12px" }}>
                Choose a project:
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                {manageableProjects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => {
                      setSelectedProjectForUpload(project.id);
                      setShowProjectSelector(false);
                    }}
                    className="console-action-btn"
                    style={{ textAlign: "left" }}
                  >
                    {project.title}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedProjectForUpload && currentUserId != null && (
        <SelfUploadModal
          projectId={selectedProjectForUpload}
          currentUserId={currentUserId}
          onSuccess={() => {
            setSelectedProjectForUpload(null);
            loadConsole();
          }}
          onClose={() => setSelectedProjectForUpload(null)}
        />
      )}
    </div>
  );
};
